/**
 * Floating shop-now button — dynamic href/label per section.
 *
 * How it works:
 *   1. Finds the floating button by `.hero-shop-now-fixed`
 *   2. Reads the global defaults from <script id="floating-shop-now-defaults">
 *   3. Finds every `<span class="js-floating-config" data-href data-label>` in the page
 *      Each one acts as a marker for its parent <section.shopify-section>
 *   4. Uses IntersectionObserver to watch those sections
 *   5. When a section becomes the dominant one in the viewport, updates the
 *      button's href / label / aria-label
 *   6. If no participating section is visible, resets to the defaults
 *
 * To make a section participate, render the `floating-shop-now-config` snippet
 * inside its content with `url` and `label` params (see hero.liquid for an example).
 */

(() => {
  const button = document.querySelector('.hero-shop-now-fixed');
  if (!button) return;

  // iOS Safari clips position:fixed elements that are inside ancestors with
  // overflow:clip. Moving the button to <body> ensures it is never inside a
  // clipping context, so it stays pinned to the viewport on real mobile devices.
  document.body.appendChild(button);

  const defaultsEl = document.getElementById('floating-shop-now-defaults');
  let defaults = { href: button.getAttribute('href'), label: button.textContent.trim() };
  if (defaultsEl) {
    try {
      defaults = { ...defaults, ...JSON.parse(defaultsEl.textContent) };
    } catch {
      /* fall through to button-derived defaults */
    }
  }

  const configs = document.querySelectorAll('.js-floating-config');
  if (!configs.length) return;

  /** @type {Map<Element, { href: string; label: string }>} */
  const sectionMap = new Map();
  for (const config of configs) {
    const section = config.closest('.shopify-section');
    if (!section) continue;
    sectionMap.set(section, {
      href: config.dataset.href || defaults.href,
      label: config.dataset.label || defaults.label,
    });
  }

  /** @type {Set<Element>} sections currently intersecting */
  const visible = new Set();

  const apply = () => {
    // Pick the section closest to the viewport center (most likely the
    // "current" one when several intersect at once).
    const viewportCenter = window.innerHeight / 2;
    let best = null;
    let bestDistance = Infinity;
    for (const section of visible) {
      const rect = section.getBoundingClientRect();
      const sectionCenter = rect.top + rect.height / 2;
      const distance = Math.abs(sectionCenter - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = section;
      }
    }

    const config = best ? sectionMap.get(best) : defaults;
    if (!config) return;

    if (button.getAttribute('href') !== config.href) {
      button.setAttribute('href', config.href);
    }
    if (config.label && button.textContent.trim() !== config.label) {
      button.textContent = config.label;
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      }
      apply();
    },
    { threshold: [0.25, 0.5, 0.75] }
  );

  for (const section of sectionMap.keys()) observer.observe(section);

  /**
   * Pin the floating button just BELOW the hero logo and keep it there.
   *
   * Strategy: the button is `position: fixed`, so once we set its `top` it
   * stays at that exact viewport spot and floats over everything while the
   * user scrolls — it does NOT move or animate. We compute that spot once
   * (and again on resize / font-load) from the hero logo's measured baseline,
   * so it sits a fixed gap below the actual letters on ANY screen size.
   */
  const heroTitle = document.querySelector('.hero-title');

  const LETTER_GAP = 30; // resting distance below the logo's letters (<= 40px)
  const EDGE_MARGIN = 8; // never let the button touch the viewport bottom

  /*
   * The hero title uses the "Ballet" script font whose glyphs carry long
   * decorative swashes that fall far below the baseline. The element's box
   * (and getBoundingClientRect) therefore extends well past the *readable*
   * letters — anchoring to the box bottom pushes the button much too low.
   *
   * Instead we anchor to the text BASELINE, computed from font metrics.
   * `baselineOffset` is the distance (px) from the title box top down to the
   * baseline; it scales with font-size, so we recompute it on load + resize.
   */
  let baselineOffset = 0;
  let measureCanvas = null;
  const measureBaselineOffset = () => {
    if (!heroTitle) return;
    const cs = getComputedStyle(heroTitle);
    const fontSize = parseFloat(cs.fontSize) || 0;
    const lineHeight = parseFloat(cs.lineHeight) || fontSize;
    const padTop = parseFloat(cs.paddingTop) || 0;
    measureCanvas ??= document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${fontSize}px ${cs.fontFamily}`;
    const m = ctx.measureText(heroTitle.textContent.trim() || 'Yakuzzi');
    const ascent = m.fontBoundingBoxAscent ?? fontSize * 0.8;
    const descent = m.fontBoundingBoxDescent ?? fontSize * 0.2;
    const halfLeading = (lineHeight - (ascent + descent)) / 2;
    baselineOffset = padTop + ascent + halfLeading;
  };

  const positionButton = () => {
    if (!heroTitle) return;

    const vh = window.innerHeight;
    const btnH = button.offsetHeight || 40;

    // Position of the logo's box top in DOCUMENT coords. Adding scrollY makes
    // this independent of the current scroll position, so the button is always
    // pinned to the spot the logo occupies at the top of the page — and since
    // the button is `position: fixed`, that becomes a fixed viewport `top`.
    const titleRect = heroTitle.getBoundingClientRect();
    const docLettersBottom = titleRect.top + window.scrollY + baselineOffset;

    let top = docLettersBottom + LETTER_GAP; // ~30px below the actual letters
    top = Math.min(top, vh - btnH - EDGE_MARGIN);
    top = Math.max(top, EDGE_MARGIN);

    button.style.top = `${Math.round(top)}px`;
    button.style.bottom = 'auto';
  };

  if (heroTitle) {
    measureBaselineOffset();
    positionButton();
    // Re-pin on resize only — the button stays put while scrolling.
    window.addEventListener(
      'resize',
      () => {
        measureBaselineOffset();
        positionButton();
      },
      { passive: true }
    );
    // Re-measure after web fonts load (the Ballet script changes the logo size).
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        measureBaselineOffset();
        positionButton();
      });
    }
  }
})();
